// ===========================================
// Charm Promo Enforcement (Pierre + Welcome Bracelet)
// ===========================================

// Config: list of charm promos to enforce
const CHARM_PROMOS = [
  {
    variantId: "46790830882982",      // Pierre + welcome bracelet bundle
    requiredCode: "CHARMED2MEETU",    // Required discount code
  },
  {
    variantId: "46715440890022",      // Second charm promo variant
    requiredCode: "CHARMEDTOMEETYOU", // Required discount code
  },
];

const PROMO_VARIANT_IDS = CHARM_PROMOS.map((p) => p.variantId);

// -------------------------------------------
// NEW: Landing config for auto-adding GWP
// -------------------------------------------

const CHARM_LANDING = {
  // This is the second promo (bracelet-only)
  variantId: "46715440890022",
  requiredCode: "CHARMEDTOMEETYOU",

  // Where the Klaviyo link sends them
  targetPath: "/collections/bestsellers",

  // Hash flag you confirmed Klaviyo preserves
  hash: "#promo_charm_bestsellers",

  // Session flag so we don’t keep re-adding
  sessionFlag: "promo_charm_bestsellers_applied",
};

let firstCharmEnforcement = true;

// -------------------------------------------
// Shared helpers
// -------------------------------------------

async function getCart() {
  const res = await fetch("/cart.js", { credentials: "same-origin" });
  return res.json();
}

/**
 * If no promo item is present, just return the cart immediately.
 * If ANY promo item IS present, optionally wait for discount codes to settle.
 */
async function getCartWithOptionalWait(retries = 5, delay = 250) {
  const initialCart = await getCart();
  const hasPromoItem = initialCart.items.some((item) =>
    PROMO_VARIANT_IDS.includes(String(item.variant_id))
  );

  if (!hasPromoItem) {
    return initialCart;
  }

  // We do have at least one promo item — wait a bit for codes to appear, to avoid race conditions
  for (let i = 0; i < retries; i++) {
    const cart = await getCart();
    const hasCode =
      Array.isArray(cart.discount_codes) && cart.discount_codes.length > 0;
    if (hasCode) return cart;
    await new Promise((r) => setTimeout(r, delay));
  }

  // If we get here, codes never showed up — just return latest cart anyway
  return getCart();
}

// -------------------------------------------
// NEW: Auto-add GWP on collection landing
// -------------------------------------------

function cleanCharmPromoHash() {
  try {
    if (
      window.location.hash === CHARM_LANDING.hash &&
      window.history &&
      window.history.replaceState
    ) {
      const newUrl = window.location.pathname + window.location.search;
      window.history.replaceState(null, "", newUrl);
    }
  } catch (e) {
    console.warn("[CharmPromo] Unable to clean hash", e);
  }
}

async function maybeAutoAddCharmPromoFromLanding() {
  try {
    // Only run on the exact target collection
    if (!window.location.pathname.startsWith(CHARM_LANDING.targetPath)) return;

    // Require the promo hash
    if (window.location.hash !== CHARM_LANDING.hash) return;

    // Avoid multiple adds per session
    if (sessionStorage.getItem(CHARM_LANDING.sessionFlag)) {
      cleanCharmPromoHash();
      return;
    }

    // Check if GWP is already in the cart
    const cart = await getCart();
    const alreadyInCart = cart.items.some(
      (item) => String(item.variant_id) === String(CHARM_LANDING.variantId)
    );

    if (alreadyInCart) {
      sessionStorage.setItem(CHARM_LANDING.sessionFlag, "true");
      cleanCharmPromoHash();
      return;
    }

    console.log(
      "[CharmPromo] Auto-adding charm promo variant from landing:",
      CHARM_LANDING.variantId
    );

    // Add the GWP variant
    const addRes = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        items: [
          {
            id: CHARM_LANDING.variantId,
            quantity: 1,
          },
        ],
      }),
    });

    if (!addRes.ok) {
      console.warn(
        "[CharmPromo] /cart/add.js returned non-OK",
        addRes.status
      );
    }

    sessionStorage.setItem(CHARM_LANDING.sessionFlag, "true");
    cleanCharmPromoHash();

    // Let SlideCart / others update if needed
    window.SLIDECART_UPDATE?.();

    // Optionally re-run enforcement soon after add
    scheduleCharmEnforcement(300);
  } catch (err) {
    console.error("[CharmPromo] Error auto-adding charm promo", err);
  }
}

// -------------------------------------------
// Enforcement logic (your existing code)
// -------------------------------------------

async function enforcePromoRules({ longWait = false } = {}) {
  console.log("[CharmPromo] Enforcing charm promo rules…");

  // Longer wait for the FIRST run (and optionally if we ever force longWait)
  const retries = longWait || firstCharmEnforcement ? 16 : 5;   // up to ~6.4s vs ~1.25s
  const delay   = longWait || firstCharmEnforcement ? 400 : 250;

  const cart = await getCartWithOptionalWait(retries, delay);
  firstCharmEnforcement = false;

  const activeCodes = (cart.discount_codes || []).map((dc) =>
    (dc.code || "").toUpperCase()
  );

  for (const item of cart.items) {
    const variantId = String(item.variant_id);

    // Only care about variants in our charm promo list
    if (!PROMO_VARIANT_IDS.includes(variantId)) continue;

    const promo = CHARM_PROMOS.find((p) => p.variantId === variantId);
    if (!promo) continue; // Safety guard

    const requiredCode = promo.requiredCode.toUpperCase();
    const hasRequiredCode = activeCodes.includes(requiredCode);

    // If the matching discount code is missing, remove the line item
    if (!hasRequiredCode) {
      console.log(
        `[CharmPromo] Removing variant ${variantId} — missing required code ${requiredCode}`
      );
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.key, quantity: 0 }),
      });

      window.SLIDECART_UPDATE?.();
      continue;
    }

    // Required code is present — enforce quantity == 1
    if (item.quantity !== 1) {
      console.log(
        `[CharmPromo] Normalizing quantity for ${variantId} from ${item.quantity} → 1`
      );
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.key, quantity: 1 }),
      });
      window.SLIDECART_UPDATE?.();
    }

    // Lock quantity controls in the UI for this specific variant
    disableQuantityControls(variantId);
  }
}

function disableQuantityControls(variantId) {
  // Slide-out cart (SlideCart HQ style)
  document
    .querySelectorAll(`[data-variant-id="${variantId}"]`)
    .forEach((line) => {
      const dec = line.querySelector('button[aria-label="decrease quantity"]');
      const inc = line.querySelector('button[aria-label="increase quantity"]');
      const input = line.querySelector(
        ".quantity-selector input[type='text'], .quantity-selector input[type='number']"
      );

      if (dec) {
        dec.disabled = true;
        dec.setAttribute("aria-disabled", "true");
      }
      if (inc) {
        inc.disabled = true;
        inc.setAttribute("aria-disabled", "true");
      }
      if (input) {
        input.readOnly = true;
        input.value = "1";
        input.classList.add("charm-promo-locked");
      }
    });

  // /cart page (Dawn-style IDs like updates_large_{{ variant.id }})
  document.querySelectorAll(".cart__qty-input").forEach((input) => {
    const match = input.id && input.id.match(/updates_large_(\d+)/);
    if (match && match[1] === String(variantId)) {
      input.readOnly = true;
      input.value = "1";
      input.classList.add("charm-promo-locked");
    }
  });
}

// --- Throttled enforcement helper ---
let charmEnforceTimeout;
function scheduleCharmEnforcement(delay = 300) {
  clearTimeout(charmEnforceTimeout);
  charmEnforceTimeout = setTimeout(() => enforcePromoRules(), delay);
}

// --- MutationObserver with debounce ---
const charmObserver = new MutationObserver(() => {
  scheduleCharmEnforcement(300);
});

charmObserver.observe(document.body, { childList: true, subtree: true });

// Initial run – force long wait on first load
document.addEventListener("DOMContentLoaded", () => {
  console.log("[CharmPromo] DOM ready — auto-add + enforcing charm promo rules…");
  // Fire the landing helper (no harm if URL/hash don't match)
  maybeAutoAddCharmPromoFromLanding();
  // Normal enforcement logic
  enforcePromoRules({ longWait: true });
});


// // ===========================================
// // Charm Promo Enforcement (Bracelets + Pierre)
// // ===========================================

// const PROMO_CONFIG = {
//   // Welcome bracelet – must NEVER be purchased alone
//   "46715440890022": {
//     requiredCode: "CHARMEDTOMEETYOU",
//     enforceNonPromoAtCheckout: true,
//   },
//   // Pierre + welcome bracelet bundle
//   "46790830882982": {
//     requiredCode: "CHARMHOLE",
//     enforceNonPromoAtCheckout: false,
//   },
// };

// const PROMO_VARIANT_IDS = Object.keys(PROMO_CONFIG);

// async function getCart() {
//   const res = await fetch("/cart.js");
//   return res.json();
// }

// /**
//  * If no promo items are present, just return the cart immediately.
//  * If promo items ARE present, optionally wait for discount codes to settle.
//  */
// async function getCartWithOptionalWait(retries = 5, delay = 250) {
//   const initialCart = await getCart();
//   const hasPromoItem = initialCart.items.some((item) =>
//     Boolean(PROMO_CONFIG[String(item.variant_id)])
//   );

//   if (!hasPromoItem) {
//     return initialCart;
//   }

//   // We do have promo items — wait a bit for codes to appear, to avoid race conditions
//   for (let i = 0; i < retries; i++) {
//     const cart = await getCart();
//     const hasCode =
//       Array.isArray(cart.discount_codes) && cart.discount_codes.length > 0;
//     if (hasCode) return cart;
//     await new Promise((r) => setTimeout(r, delay));
//   }

//   // If we get here, codes never showed up — just return latest cart anyway
//   return getCart();
// }

// async function enforcePromoRules() {
//   console.log("[CharmPromo] Enforcing charm promo rules…");

//   const cart = await getCartWithOptionalWait();

//   const activeCodes = (cart.discount_codes || []).map((dc) =>
//     (dc.code || "").toUpperCase()
//   );

//   // Count non-promo items (for "bracelet must not be alone at checkout")
//   const nonPromoItemCount = cart.items
//     .filter((item) => !PROMO_VARIANT_IDS.includes(String(item.variant_id)))
//     .reduce((sum, item) => sum + item.quantity, 0);

//   for (const item of cart.items) {
//     const variantId = String(item.variant_id);
//     const config = PROMO_CONFIG[variantId];
//     if (!config) continue; // Not one of our promo variants

//     const requiredCode = config.requiredCode.toUpperCase();
//     const hasRequiredCode = activeCodes.includes(requiredCode);

//     // If the matching discount code is missing, remove the line item
//     if (!hasRequiredCode) {
//       console.log(
//         `[CharmPromo] Removing variant ${variantId} — missing required code ${requiredCode}`
//       );
//       await fetch("/cart/change.js", {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ id: item.key, quantity: 0 }),
//       });

//       window.SLIDECART_UPDATE?.();
//       continue;
//     }

//     // Required code is present — enforce quantity == 1
//     if (item.quantity !== 1) {
//       console.log(
//         `[CharmPromo] Normalizing quantity for ${variantId} from ${item.quantity} → 1`
//       );
//       await fetch("/cart/change.js", {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ id: item.key, quantity: 1 }),
//       });
//       window.SLIDECART_UPDATE?.();
//     }

//     // Lock quantity controls in the UI
//     disableQuantityControls(variantId);
//   }

//   // Attach checkout guards (runs once, but uses fresh cart when clicked)
//   attachCheckoutGuards();
// }

// function disableQuantityControls(variantId) {
//   // Slide-out cart (SlideCart HQ style)
//   document
//     .querySelectorAll(`[data-variant-id="${variantId}"]`)
//     .forEach((line) => {
//       const dec = line.querySelector('button[aria-label="decrease quantity"]');
//       const inc = line.querySelector('button[aria-label="increase quantity"]');
//       const input = line.querySelector(
//         ".quantity-selector input[type='text'], .quantity-selector input[type='number']"
//       );

//       if (dec) {
//         dec.disabled = true;
//         dec.setAttribute("aria-disabled", "true");
//       }
//       if (inc) {
//         inc.disabled = true;
//         inc.setAttribute("aria-disabled", "true");
//       }
//       if (input) {
//         input.readOnly = true;
//         input.value = "1";
//         input.classList.add("charm-promo-locked");
//       }
//     });

//   // /cart page (Dawn-style IDs like updates_large_{{ variant.id }})
//   document.querySelectorAll(".cart__qty-input").forEach((input) => {
//     const match = input.id && input.id.match(/updates_large_(\d+)/);
//     if (match && match[1] === String(variantId)) {
//       input.readOnly = true;
//       input.value = "1";
//       input.classList.add("charm-promo-locked");
//     }
//   });
// }

// // --- Checkout guards (prevent bracelet-only checkout) ---

// let checkoutGuardsAttached = false;

// function attachCheckoutGuards() {
//   if (checkoutGuardsAttached) return;
//   checkoutGuardsAttached = true;

//   const selectors = [
//     'form[action="/cart"] [name="checkout"]',
//     'button[name="checkout"]',
//     'a[href*="/checkout"]',
//     '#slidecarthq button[name="checkout"]',
//   ];

//   const handleClick = async (event) => {
//     try {
//       const cart = await getCart();

//       const hasBracelet = cart.items.some(
//         (item) => String(item.variant_id) === "46715440890022"
//       );

//       if (!hasBracelet) {
//         // No bracelet in cart — nothing to enforce here
//         return;
//       }

//       const braceletConfig = PROMO_CONFIG["46715440890022"];
//       if (!braceletConfig || !braceletConfig.enforceNonPromoAtCheckout) {
//         return;
//       }

//       const nonPromoItemCount = cart.items
//         .filter(
//           (item) =>
//             !PROMO_VARIANT_IDS.includes(String(item.variant_id)) &&
//             item.quantity > 0
//         )
//         .reduce((sum, item) => sum + item.quantity, 0);

//       // If bracelet is effectively alone in the cart, block checkout
//       if (nonPromoItemCount < 1) {
//         event.preventDefault();
//         event.stopPropagation();

//         // Optional: also remove the bracelet so the user clearly sees what happened
//         // await fetch("/cart/change.js", {
//         //   method: "POST",
//         //   headers: { "Content-Type": "application/json" },
//         //   body: JSON.stringify({ id: braceletLine.key, quantity: 0 }),
//         // });
//         // window.SLIDECART_UPDATE?.();

//         alert(
//           "Your free charm can only be claimed with a Wooble in your cart. Please add a kit to continue."
//         );
//         return false;
//       }
//     } catch (e) {
//       console.error("[CharmPromo] Error in checkout guard", e);
//     }
//   };

//   // Attach listeners to all current checkout buttons/links
//   document.querySelectorAll(selectors.join(",")).forEach((el) => {
//     // Avoid double-binding on rerenders
//     if (el.dataset.charmGuardBound === "true") return;
//     el.addEventListener("click", handleClick);
//     el.dataset.charmGuardBound = "true";
//   });
// }

// // --- Throttled enforcement helper ---
// let charmEnforceTimeout;
// function scheduleCharmEnforcement(delay = 300) {
//   clearTimeout(charmEnforceTimeout);
//   charmEnforceTimeout = setTimeout(() => enforcePromoRules(), delay);
// }

// // --- MutationObserver with debounce ---
// const charmObserver = new MutationObserver(() => {
//   scheduleCharmEnforcement(300);
//   // Re-scan for checkout buttons when DOM changes (slidecart open/close etc.)
//   attachCheckoutGuards();
// });

// charmObserver.observe(document.body, { childList: true, subtree: true });

// // Initial run
// document.addEventListener("DOMContentLoaded", () => {
//   console.log("[CharmPromo] DOM ready — enforcing charm promo rules…");
//   enforcePromoRules();
//   attachCheckoutGuards();
// });
